import { defineStore } from "pinia";
import { computed, ref } from "vue";
import axios from "axios";

import {
  AUTH0_BASE_PATH,
  AUTH0_USER_PROFILE_PATH,
  buildAuth0LoginUrl,
  buildAuth0LogoutUrl,
  type Auth0OrganisationProfile,
  type Auth0Session,
  type Auth0SessionUser,
  type Auth0UserProfile,
  type BuildLoginUrlParams,
} from "@/utils/auth0";

/**
 * Auth0 RWA (cookie) session store.
 *
 * The session cookies (`csiq_<env>_session` plus a refresh cookie scoped to
 * `/api/auth/refresh`) are httpOnly and managed by the edge; the frontend never
 * reads, sets or clears them — only `logout` below does, by redirect. We use a
 * bare axios call (with
 * credentials) rather than the app `$http` instance so we bypass the JWT Bearer
 * request interceptor and its 401 -> signOut response side-effects. The session
 * endpoint always returns 200 (`{ authenticated:false }` when anonymous), so it
 * is safe to poll on app load and in the router guard.
 */
const useAuth0SessionStore = defineStore("auth0Session", () => {
  const authenticated = ref<boolean>(false);
  const user = ref<Auth0SessionUser | null>(null);
  const roles = ref<string[]>([]);
  const permissions = ref<string[]>([]);
  const loaded = ref<boolean>(false);
  const loading = ref<boolean>(false);

  // The user profile (identity + nested organisation) for the signed-in
  // identity, fetched separately from the session — the httpOnly cookie carries
  // no token the SPA can decode, so this endpoint is the only source of the user
  // name/email/uuid/roles AND the org uuid + account type used for routing.
  const profile = ref<Auth0UserProfile | null>(null);
  const profileLoaded = ref<boolean>(false);

  /** Convenience view over `profile.organisation` for route resolution. */
  const organisation = computed<Auth0OrganisationProfile | null>(
    () => profile.value?.organisation ?? null,
  );

  // Tracks the in-flight fetch so concurrent callers (e.g. App.vue bootstrap
  // and the router guard on initial load) share one request instead of firing
  // duplicate GET /session calls. Not reactive — purely a dedup handle.
  let inflight: Promise<Auth0Session> | null = null;
  let profileInflight: Promise<Auth0UserProfile | null> | null = null;

  function applySession(session: Auth0Session | null) {
    authenticated.value = Boolean(session?.authenticated);
    user.value = session?.user ?? null;
    roles.value = session?.roles ?? [];
    permissions.value = session?.permissions ?? [];
  }

  function snapshot(): Auth0Session {
    return {
      authenticated: authenticated.value,
      user: user.value,
      roles: roles.value,
      permissions: permissions.value,
    };
  }

  function reset() {
    applySession(null);
    loaded.value = false;
    inflight = null;
    profile.value = null;
    profileLoaded.value = false;
    profileInflight = null;
  }

  async function fetchSession(): Promise<Auth0Session> {
    loading.value = true;
    try {
      const { data } = await axios.get<Auth0Session>(
        `${AUTH0_BASE_PATH}/session`,
        { withCredentials: true },
      );
      applySession(data);
      loaded.value = true;
      return data;
    } catch {
      // The endpoint never 401s; a network/other failure means "not
      // authenticated" for our purposes. Never throw from a guard/bootstrap path.
      applySession(null);
      loaded.value = true;
      return { authenticated: false };
    } finally {
      loading.value = false;
    }
  }

  /**
   * Fetch the session at most once; reuse the cached result thereafter.
   * Concurrent callers before the first fetch resolves share one request.
   */
  async function ensureSession(): Promise<Auth0Session> {
    if (loaded.value) return snapshot();
    if (!inflight) {
      inflight = fetchSession().finally(() => {
        inflight = null;
      });
    }
    return inflight;
  }

  /**
   * Fetch the user profile (identity + organisation) for the signed-in
   * identity. Uses a bare axios call (with credentials) so it relies on the
   * session cookie and bypasses the JWT Bearer interceptor. Any failure
   * (401/404/5xx) resolves to null — never throws, so it is safe to call from a
   * guard.
   */
  async function fetchProfile(): Promise<Auth0UserProfile | null> {
    try {
      const { data } = await axios.get<Auth0UserProfile>(
        AUTH0_USER_PROFILE_PATH,
        { withCredentials: true },
      );
      profile.value = data;
      profileLoaded.value = true;
      return data;
    } catch {
      profile.value = null;
      profileLoaded.value = true;
      return null;
    }
  }

  /**
   * Fetch the user profile at most once; reuse the cached result thereafter.
   * Concurrent callers before the first fetch resolves share one request.
   */
  async function ensureProfile(): Promise<Auth0UserProfile | null> {
    if (profileLoaded.value) return profile.value;
    if (!profileInflight) {
      profileInflight = fetchProfile().finally(() => {
        profileInflight = null;
      });
    }
    return profileInflight;
  }

  /** Full-page redirect to the Auth0 login endpoint. */
  function login(
    returnPath?: string | null,
    extra: Omit<BuildLoginUrlParams, "returnPath"> = {},
  ) {
    const url = buildAuth0LoginUrl({ returnPath, ...extra });
    globalThis.location.assign(url);
  }

  /**
   * End the Auth0 session: full-page redirect to the logout endpoint, which
   * clears the session cookies and 302s on to the tenant's `/v2/logout`.
   *
   * This has to be a top-level navigation, exactly like `login`. An XHR cannot
   * do it: the Auth0 session cookie lives on the tenant's own domain, so only a
   * real browser redirect to `/v2/logout` can clear it (and Auth0 sends no CORS
   * headers, so the request would fail anyway). Clearing our cookie alone
   * leaves the IdP session alive and the next login silently re-authenticates —
   * the user would never actually be signed out.
   */
  function logout(returnUrl?: string | null): void {
    // Reset first: the navigation tears this page down regardless, and a
    // blocked/slow redirect must not leave a stale "authenticated" store.
    reset();
    globalThis.location.assign(buildAuth0LogoutUrl(returnUrl));
  }

  return {
    authenticated,
    user,
    roles,
    permissions,
    loaded,
    loading,
    profile,
    organisation,
    fetchSession,
    ensureSession,
    fetchProfile,
    ensureProfile,
    login,
    logout,
    reset,
  };
});

export { useAuth0SessionStore };
